"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/lib/auth/AuthContext";
import { SignInAbortedError, SignUpAbortedError } from "@/lib/auth/AuthProvider";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { getAuthErrorMessage } from "@/lib/auth/authErrorMessage";
import { useTenantSettings, useFeatureFlag } from "@/lib/tenant/TenantSettingsProvider";

interface SignInPageProps {
    locale: string;
}

/**
 * Sign-in page component.
 *
 * Key differences from Ballon d'Or:
 * - Uses our multi-tenant auth context
 * - Integrates with our label system instead of next-intl
 * - Follows our page component patterns
 */
export function SignInPage({ locale }: SignInPageProps) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const labels = useLabels();
    const { isMultiLang } = useTenantSettings();
    const isAuthFeatureEnabled = useFeatureFlag("authentication");
    const { signIn, signInWithGoogle, user, loading, isAuthEnabled } = useAuth();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [error, setError] = useState("");

    // Get localized strings from labels
    const t = labels.auth?.signIn ?? {};
    // Only prefix URLs with locale if tenant has multiple languages
    const basePath = isMultiLang && locale ? `/${locale}` : "";

    // Redirect if already authenticated
    useEffect(() => {
        if (!loading && user && !user.isAnonymous) {
            const returnUrl = searchParams.get("returnUrl") || basePath || "/";
            router.push(returnUrl);
        }
    }, [user, loading, router, basePath, searchParams]);

    // If auth feature is disabled by super-admin or not configured, show a message
    if (!isAuthFeatureEnabled || !isAuthEnabled) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4">
                <div className="text-center text-muted-foreground">Authentication is not configured for this site.</div>
            </div>
        );
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError("");

        try {
            await signIn(email, password);
        } catch (err: unknown) {
            if (err instanceof SignInAbortedError) {
                setIsSubmitting(false);
                return;
            }
            setError(getAuthErrorMessage(err, labels) ?? "");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError("");
        setIsGoogleLoading(true);
        try {
            await signInWithGoogle();
        } catch (err: unknown) {
            if (err instanceof SignUpAbortedError) {
                setIsGoogleLoading(false);
                return;
            }
            setError(getAuthErrorMessage(err, labels) ?? "");
        } finally {
            setIsGoogleLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 bg-background">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-foreground">{t.title || "Welcome Back"}</h1>
                    <p className="mt-2 text-muted-foreground">{t.subtitle || "Sign in to your account"}</p>
                </div>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
                                {t.email || "Email"}
                            </label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full px-3 py-2 input"
                                placeholder="you@example.com"
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
                                {t.password || "Password"}
                            </label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full px-3 py-2 input"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="text-red-500 text-sm text-center bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isSubmitting || isGoogleLoading}
                        className="w-full py-3 px-4 font-medium btn-primary disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
                    >
                        {isSubmitting && (
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                            </svg>
                        )}
                        {isSubmitting ? t.signingIn || "Signing in..." : t.signInButton || "Sign In"}
                    </button>

                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-background text-muted-foreground">
                                {t.orContinueWith || "Or continue with"}
                            </span>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        disabled={isSubmitting || isGoogleLoading}
                        className="w-full py-3 px-4 border border-border rounded-lg font-medium text-foreground hover:bg-muted transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isGoogleLoading ? (
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path
                                    fill="currentColor"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                            </svg>
                        )}
                        {isGoogleLoading
                            ? t.signingInWithGoogle || "Signing in..."
                            : t.continueWithGoogle || "Continue with Google"}
                    </button>
                </form>

                <div className="text-center space-y-2">
                    <Link
                        prefetch={false}
                        href={`${basePath}/forgot-password`}
                        className="text-sm text-primary hover:underline cursor-pointer"
                    >
                        {t.forgotPassword || "Forgot password?"}
                    </Link>
                    <p className="text-sm text-muted-foreground">
                        {t.noAccount || "Don't have an account?"}{" "}
                        <Link
                            prefetch={false}
                            href={`${basePath}/sign-up`}
                            className="text-primary hover:underline cursor-pointer"
                        >
                            {t.signUpLink || "Sign up"}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
